WAU.ThemeCart = (function (exports) {
  'use strict';

  function getDefaultRequestConfig() {
    return JSON.parse(
      JSON.stringify({
        credentials: 'same-origin',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Content-Type': 'application/json;'
        }
      })
    );
  }

  function fetchJSON(url, config) {
    return fetch(url, config).then(function(response) {
      if (!response.ok) {
        throw response;
      }
      return response.json();
    });
  }

  function cart() {
    return fetchJSON('/cart.js', getDefaultRequestConfig());
  }

  function cartAdd(id, quantity, properties) {
    var config = getDefaultRequestConfig();

    config.method = 'POST';
    config.body = JSON.stringify({
      id: id,
      quantity: quantity,
      properties: properties
    });

    return fetchJSON('/cart/add.js', config);
  }

  function cartAddFromForm(formData) {
    var config = getDefaultRequestConfig();
    delete config.headers['Content-Type'];

    config.method = 'POST';
    config.body = formData;

    return fetchJSON('/cart/add.js', config);
  }

  function cartChange(line, options) {
    var config = getDefaultRequestConfig();

    options = options || {};

    config.method = 'POST';
    config.body = JSON.stringify({
      line: line,
      quantity: options.quantity,
      properties: options.properties
    });

    return fetchJSON('/cart/change.js', config);
  }

  function cartClear() {
    var config = getDefaultRequestConfig();
    config.method = 'POST';

    return fetchJSON('/cart/clear.js', config);
  }

  function cartUpdate(body) {
    var config = getDefaultRequestConfig();

    config.method = 'POST';
    config.body = JSON.stringify(body);

    return fetchJSON('/cart/update.js', config);
  }

  function cartShippingRates() {
    return fetchJSON('/cart/shipping_rates.json', getDefaultRequestConfig());
  }

  function key(key) {
    if (typeof key !== 'string' || key.split(':').length !== 2) {
      throw new TypeError(
        'Theme Cart: Provided key value is not a string with the format xxx:xxx'
      );
    }
  }

  function quantity(quantity) {
    if (typeof quantity !== 'number' || isNaN(quantity)) {
      throw new TypeError(
        'Theme Cart: An object which specifies a quantity or properties value is required'
      );
    }
  }

  function id(id) {
    if (typeof id !== 'number' || isNaN(id)) {
      throw new TypeError('Theme Cart: Variant ID must be a number');
    }
  }

  function properties(properties) {
    if (typeof properties !== 'object') {
      throw new TypeError('Theme Cart: Properties must be an object');
    }
  }

  function form(form) {
    if (!(form instanceof HTMLFormElement)) {
      throw new TypeError('Theme Cart: Form must be an instance of HTMLFormElement');
    }
  }

  function options(options) {
    if (typeof options !== 'object') {
      throw new TypeError('Theme Cart: Options must be an object');
    }

    if (
      typeof options.quantity === 'undefined' &&
      typeof options.properties === 'undefined'
    ) {
      throw new Error(
        'Theme Cart: You muse define a value for quantity or properties'
      );
    }

    if (typeof options.quantity !== 'undefined') {
      quantity(options.quantity);
    }

    if (typeof options.properties !== 'undefined') {
      properties(options.properties);
    }
  }

  /**
   * Cart Template Script
   * ------------------------------------------------------------------------------
   * A file that contains scripts highly couple code to the Cart template.
   *
   * @namespace cart
   */

  /**
   * Returns the state object of the cart
   * @returns {Promise} Resolves with the state object of the cart (https://help.shopify.com/en/themes/development/getting-started/using-ajax-api#get-cart)
   */
  function getState() {
    return cart().then(function(cart) {
      Events.trigger("cart:ready", cart)
    });
  }

  /**
   * Returns the object of the cart
   * @returns {Promise} Resolves with the object of the cart (https://help.shopify.com/en/themes/development/getting-started/using-ajax-api#get-cart)
   */
  function getCart() {
    return cart().then(function(cart) {
      return cart;
    });
  }

  /**
   * Returns the index of the cart line item
   * @param {string} key The unique key of the line item
   * @returns {Promise} Resolves with the index number of the line item
   */
  function getItemIndex(key$$1) {
    key(key$$1);

    return cart().then(function(state) {
      var index = -1;

      state.items.forEach(function(item, i) {
        index = item.key === key$$1 ? i + 1 : index;
      });

      if (index === -1) {
        return Promise.reject(
          new Error('Theme Cart: Unable to match line item with provided key')
        );
      }

      return index;
    });
  }

  /**
   * Fetches the line item object
   * @param {string} key The unique key of the line item
   * @returns {Promise} Resolves with the line item object (See response of cart/add.js https://help.shopify.com/en/themes/development/getting-started/using-ajax-api#add-to-cart)
   */
  function getItem(key$$1) {
    key(key$$1);

    return cart().then(function(state) {
      var lineItem = null;

      state.items.forEach(function(item) {
        lineItem = item.key === key$$1 ? item : lineItem;
      });

      if (lineItem === null) {
        return Promise.reject(
          new Error('Theme Cart: Unable to match line item with provided key')
        );
      }
      return lineItem;
    });
  }

  /**
   * Add a new line item to the cart
   * @param {number} id The variant's unique ID
   * @param {object} options Optional values to pass to /cart/add.js
   * @param {number} options.quantity The quantity of items to be added to the cart
   * @param {object} options.properties Line item property key/values (https://help.shopify.com/en/themes/liquid/objects/line_item#line_item-properties)
   * @returns {Promise} Resolves with the line item object (See response of cart/add.js https://help.shopify.com/en/themes/development/getting-started/using-ajax-api#add-to-cart)
   */
  function addItem(id$$1, options$$1) {
    options$$1 = options$$1 || {};

    id(id$$1);

    return cartAdd(id$$1, options$$1.quantity, options$$1.properties);
  }

  /**
   * Add a new line item to the cart from a product form
   * @param {object} form DOM element which is equal to the <form> node
   * @returns {Promise} Resolves with the line item object (See response of cart/add.js https://help.shopify.com/en/themes/development/getting-started/using-ajax-api#add-to-cart)
   */
  function addItemFromForm(form$$1) {
    form(form$$1);

    var formData = new FormData(form$$1);
    id(parseInt(formData.get('id'), 10));

    return cartAddFromForm(formData);
  }

  /**
   * Changes the quantity and/or properties of an existing line item.
   * @param {string} key The unique key of the line item (https://help.shopify.com/en/themes/liquid/objects/line_item#line_item-key)
   * @param {object} options Optional values to pass to /cart/add.js
   * @param {number} options.quantity The quantity of items to be added to the cart
   * @param {object} options.properties Line item property key/values (https://help.shopify.com/en/themes/liquid/objects/line_item#line_item-properties)
   * @returns {Promise} Resolves with the state object of the cart (https://help.shopify.com/en/themes/development/getting-started/using-ajax-api#get-cart)
   */
  function updateItem(key$$1, options$$1) {
    key(key$$1);
    options(options$$1);

    return getItemIndex(key$$1).then(function(line) {
      return cartChange(line, options$$1);
    });
  }

  /**
   * Removes a line item from the cart
   * @param {string} key The unique key of the line item (https://help.shopify.com/en/themes/liquid/objects/line_item#line_item-key)
   * @returns {Promise} Resolves with the state object of the cart (https://help.shopify.com/en/themes/development/getting-started/using-ajax-api#get-cart)
   */
  function removeItem(key$$1) {
    key(key$$1);

    return getItemIndex(key$$1).then(function(line) {
      return cartChange(line, { quantity: 0 });
    });
  }

  /**
   * Sets all quantities of all line items in the cart to zero. This does not remove cart attributes nor the cart note.
   * @returns {Promise} Resolves with the state object of the cart (https://help.shopify.com/en/themes/development/getting-started/using-ajax-api#get-cart)
   */
  function clearItems() {
    return cartClear().then(function() {
      Events.trigger("cart:clear");
    });
  }

  /**
   * Gets all cart attributes
   * @returns {Promise} Resolves with the cart attributes object
   */
  function getAttributes() {
    return cart().then(function(state) {
      return state.attributes;
    });
  }

  /**
   * Sets all cart attributes
   * @returns {Promise} Resolves with the cart state object
   */
  function updateAttributes(attributes) {
    return cartUpdate({ attributes: attributes });
  }

  /**
   * Clears all cart attributes
   * @returns {Promise} Resolves with the cart state object
   */
  function clearAttributes() {
    return getAttributes().then(function(attributes) {
      for (var key$$1 in attributes) {
        attributes[key$$1] = '';
      }
      return updateAttributes(attributes);
    });
  }

  /**
   * Gets cart note
   * @returns {Promise} Resolves with the cart note string
   */
  function getNote() {
    return cart().then(function(state) {
      return state.note;
    });
  }

  /**
   * Sets cart note
   * @returns {Promise} Resolves with the cart state object
   */
  function updateNote(note) {
    return cartUpdate({ note: note });
  }

  /**
   * Clears cart note
   * @returns {Promise} Resolves with the cart state object
   */
  function clearNote() {
    return cartUpdate({ note: '' });
  }

  /**
   * Get estimated shipping rates.
   * @returns {Promise} Resolves with response of /cart/shipping_rates.json (https://help.shopify.com/en/themes/development/getting-started/using-ajax-api#get-shipping-rates)
   */
  function getShippingRates() {
    return cartShippingRates();
  }

  exports.getState = getState;
  exports.getCart = getCart;
  exports.getItemIndex = getItemIndex;
  exports.getItem = getItem;
  exports.addItem = addItem;
  exports.addItemFromForm = addItemFromForm;
  exports.getDefaultRequestConfig = getDefaultRequestConfig;
  exports.updateItem = updateItem;
  exports.removeItem = removeItem;
  exports.clearItems = clearItems;
  exports.getAttributes = getAttributes;
  exports.updateAttributes = updateAttributes;
  exports.clearAttributes = clearAttributes;
  exports.getNote = getNote;
  exports.updateNote = updateNote;
  exports.clearNote = clearNote;
  exports.getShippingRates = getShippingRates;

  return exports;

}({}));

WAU.AjaxCart = {
  init: function init() {
    let config = document.getElementById('cart-config');
    if ( !config ) return false;
    config = JSON.parse(config.innerHTML || '{}');

    var selectors = {
      cartTrigger: '.js-mini-cart-trigger',
      cartPageLoader: '.ajax-cart__page-wrapper .js-mini-cart-loader',
      addToCart: '.js-ajax-submit'
    };

    // Init carts
    this.initializeAjaxCart(config);

		// Set No Js Cart or Remove
		if (config.cart_action == 'no_js_cart') {
			if ( document.querySelector('.js-ajax-cart-content') ) {
				document.querySelector('.js-ajax-cart-content').remove();
			}
		} else {
			if ( document.querySelector('.js-ajax-cart-content-nojs') ){
				document.querySelector('.js-ajax-cart-content-nojs').remove();
			}
		}

    // Override add to cart form
    document.querySelectorAll(selectors.addToCart).forEach((element, i) => {
		  element.addEventListener('click', function (e) {
        e.preventDefault();

        var addToCartForm = this.closest('form');
        WAU.AjaxCart.addToCart(addToCartForm, element.parentNode, config, false, false);

        return false;
      });
    });

    // Update cart page on load
    if (document.querySelector('body').classList.contains('template-cart')) {
      WAU.ThemeCart.getCart().then(Cart => {
        WAU.AjaxCart.updateView(config, Cart);

        // Remove page loader
        setTimeout(function(){
          document.querySelector('body').classList.add('cart-loaded');
        }, 800);

      });
    }

    // On cart trigger click
    let cartTriggers = document.querySelectorAll(selectors.cartTrigger);
    cartTriggers.forEach((item, i) => {
      item.addEventListener('click', function (e) {
        e.preventDefault();

        // Open drawer
        WAU.ThemeCart.getCart().then(Cart => {

          WAU.AjaxCart.updateView(config, Cart);

          if (config.cart_action == 'drawer') {
            WAU.AjaxCart.showDrawer(config);
          } else if (config.cart_action == 'modal_cart')  {
            WAU.AjaxCart.showModal(config);
          } else {
            window.location.href = config.cart_url;
          }

        });

        return false;
      });
    });

  },
  cartEvents: function cartEvents(config) {
    var selectors = {
      cartDrawerRemove: '.js-cart-remove',
			cartDrawerQty: '[data-item-qty]',
      cartDrawerQtyDecrease: '[data-ajax-qty-decrease]',
      cartDrawerQtyIncrease: '[data-ajax-qty-increase]',
      cartNote: '.js-cart-note'
    };

    // Cart Events
		document.querySelectorAll(selectors.cartDrawerQty).forEach((element, i) => {
      element.addEventListener('change', function (e) {
        e.preventDefault();

        var quantity = parseInt(this.value),
						itemKey = this.dataset.itemKey,
						itemMax = this.dataset.limit,
						lineElement = element.closest('.ajax-cart__cart-item');

				// Set new quantity
				element.value = quantity;

				// Adjust cart object
				setTimeout(function() {
					if (quantity === 0) {
						WAU.AjaxCart.removeFromCart(itemKey, config);
					} else {

						WAU.AjaxCart.checkLimit(itemMax, quantity, lineElement, config);

						WAU.ThemeCart.updateItem(itemKey, { quantity }).then(state => {
							WAU.AjaxCart.updateView(config, state);
						});
					}
				}, 250);


        return false;
      });
    });
    document.querySelectorAll(selectors.cartDrawerRemove).forEach((element, i) => {
      element.addEventListener('click', function (e) {
        e.preventDefault();

        var itemKey = this.dataset.itemKey;

        WAU.AjaxCart.removeFromCart(itemKey, config);

        return false;
      });
    });
    document.querySelectorAll(selectors.cartDrawerQtyDecrease).forEach((element, i) => {
      element.addEventListener('click', function (e) {
        e.preventDefault();

				if (this.nextElementSibling.value === '1') {
					var itemKey = this.dataset.itemKey;
					WAU.AjaxCart.removeFromCart(itemKey, config);
				} else {
					var itemId = this.dataset.ajaxQtyDecrease;
	        WAU.AjaxCart.adjustQty(-1, itemId, config);
				}

        return false;
      });
    });
    document.querySelectorAll(selectors.cartDrawerQtyIncrease).forEach((element, i) => {
      element.addEventListener('click', function (e) {
        e.preventDefault();

        var itemId = this.dataset.ajaxQtyIncrease;
        WAU.AjaxCart.adjustQty(+1, itemId, config);

        return false;
      });
    });
    document.querySelectorAll(selectors.cartNote).forEach((element, i) => {
			element.addEventListener('blur', (event) => {
        let note = element.value;
        WAU.ThemeCart.updateNote(note).then(state => {
          WAU.AjaxCart.updateView(config, state);
        });
      });
    });

    // Reinit shipping calc
    if ( config.show_calculator && document.querySelector('body').classList.contains('template-cart')) {
      setTimeout(function(){
        ShippingCalculator.init();
      }, 1000);
    }

    // Restart Payment buttons
    if (Shopify && Shopify.StorefrontExpressButtons) {
      Shopify.StorefrontExpressButtons.initialize();
    }
  },
  showDrawer: function showDrawer(config) {
    if (config.cart_action != 'drawer') return false;

    WAU.Slideout._openByName("slideout-ajax-cart");
  },
  hideDrawer: function hideDrawer(config) {
    if (config.cart_action != 'drawer') return false;

    WAU.Slideout._closeByName("slideout-ajax-cart");
  },
  showModal: function showModal(config) {
    if (config.cart_action != 'modal_cart') return false;

    WAU.Modal._openByName("modal-ajax-cart");
  },
  hideModal: function hideModal(config) {
    if (config.cart_action != 'modal_cart') return false;

    WAU.Modal._closeByName("modal-ajax-cart");
  },
  removeFromCart: function removeFromCart(itemKey, config, callback) {
    WAU.ThemeCart.removeItem(itemKey).then(state => {
      WAU.AjaxCart.updateView(config, state);
    });
  },
  adjustQty: function adjustQty(value, itemId, config) {

    var selectors = {
      lineItem: '.item_' + itemId,
      updatesItem: '.updates_' + itemId
    };

    // Update Line Item
    document.querySelectorAll(selectors.lineItem).forEach((element, i) => {
      elementInput = element.querySelector(selectors.updatesItem),
      key = elementInput.dataset.itemKey,
      max = elementInput.dataset.limit,
      quantity = parseInt(elementInput.value) + parseInt(value);

      // Check limit to prevent over adding
			if (WAU.AjaxCart.checkLimit(max, quantity, element, config)) return false;

      // Check new qty to prevent going lower than 1
      if (quantity === 0 ) return false;

      // Set new quantity
      elementInput.value = quantity;

      // Adjust cart object
      setTimeout(function() {
        WAU.ThemeCart.updateItem(key, { quantity }).then(state => {
          WAU.AjaxCart.updateView(config, state);
        });
      }, 250);

    });
  },
	checkLimit: function checkLimit(max, quantity, element, config) {
		// Check limit to prevent over adding
		if ( max != '' && quantity > max ) {
			let cartNote = document.createElement("div");
					cartNote.classList.add('mini-cart__cart-note');
					cartNote.innerHTML = '<p class="a-center"><strong>' + config.cart_error + '</strong> ' + config.update_qty_error + '</p>';

			let adjacentElement = element.querySelector('.js-item-quantity');
			let parentElement = adjacentElement.parentNode;

			parentElement.appendChild(cartNote, adjacentElement);

			setTimeout(function() {
				WAU.Helpers.fadeOut(cartNote);
				parentElement.removeChild(cartNote);
			}, 2000);

			return true;
		}
	},
  addToCart: function addToCart(addToCartForm, formContext, config, isQuickview, quickAdd) {
    var selectors = {
      addToCart: '.js-ajax-submit',
      addToCartText: '[data-add-to-cart-text]',
      cartAddedMsg: '.js-added-msg'
    } 

					// Open drawer
					WAU.ThemeCart.getCart().then(Cart => {

						WAU.AjaxCart.updateView(config, Cart);
	
						if (config.cart_action == 'drawer') {
							WAU.AjaxCart.showDrawer(config);
						} else if (config.cart_action == 'modal_cart') {
							WAU.AjaxCart.showModal(config);
						} else {
							window.location.href = config.cart_url;
						}
	
					});

		// Reset error message
		//document.querySelector('.js-error-msg').innerHTML = `<p><b>${config.cart_error}</b>&nbsp;&nbsp;${config.update_qty_error}</p>`;

    let context;

    if ( isQuickview ) { context = document.getElementById('quickview-form') } else { context = formContext; }

    // Check if we have a recipient form.
    const isRecipientForm = [...addToCartForm.elements].filter(element => {
      return element.name == 'properties[__shopify_send_gift_card_to_recipient]' && element.checked;
    }).length !== 0;

    if (isRecipientForm) {

      const recipientConfig = WAU.ThemeCart.getDefaultRequestConfig();
      const formData = new FormData(addToCartForm);

      delete recipientConfig.headers['Content-Type'];
      recipientConfig.method = 'POST';
      recipientConfig.body = formData;

      fetch('/cart/add.js', recipientConfig)
        .then(function(response) {
          return response.json();
        }).then(function(data) {
          if (data.status == 422) {
            const errors = data.description;
            const messages  = data.message;
            Events.trigger("recipientform:errors", messages, errors, config, addToCartForm);
          }
        }).catch(function(error) {
          console.error(error);
        });
    }

    // Check line item properties required
    if (addToCartForm.elements.length > 0) {

      // Get the required "custom property" fields
      const requiredFields = [...addToCartForm.elements].filter((element) => {
        return element.hasAttribute('data-product-property') && element.required;
      });

      if (requiredFields.length > 0) {

        let required = false;

        requiredFields.forEach((requiredField) => {

          addToCartForm.elements[requiredField.name].classList.remove('required-error');

          if (requiredField.value === '') {

            required = true;

            addToCartForm.elements[requiredField.name].classList.add('required-error');

          }
        });
        if (required) return false;
      }
    };

    // Disable add to cart button temporarily
    context.querySelector(selectors.addToCartText).innerHTML = config.adding_to_cart;
    context.querySelector(selectors.addToCart).classList.add('disabled');
    context.querySelector(selectors.addToCart).getAttribute('disabled', 'disabled');


    function addingToCart(trigger) {
      if (!trigger) {
        console.warn('Warning. No trigger element passed in.');
      }
      trigger.innerHTML = config.adding_to_cart;
      trigger.classList.add('disabled');
      trigger.setAttribute('disabled', true);
    }

    function addedToCart(trigger) {
      if (!trigger) {
        console.warn('Warning. No trigger element passed in.');
      }
      trigger.innerHTML = config.added_to_cart;
      trigger.classList.remove('disabled');
      trigger.removeAttribute('disabled');
    }


    WAU.ThemeCart.addItemFromForm(addToCartForm).then(item => {
      // Re-enable add to cart button
      context.querySelector(selectors.addToCartText).innerHTML = config.added_to_cart;
      context.querySelector(selectors.addToCart).classList.remove('disabled');
      context.querySelector(selectors.addToCart).removeAttribute('disabled', 'disabled');

      setTimeout(function(){
				if ( context.querySelector(selectors.addToCart) ) context.querySelector(selectors.addToCartText).innerHTML = config.add_to_cart;
      }, 3000);

      WAU.ThemeCart.getCart().then(Cart => {

				if ( isQuickview && config.cart_added_event != 'product_page') {
					let type = context.getAttribute('data-quickshop-type');

					if ( quickAdd === false ) {
						if ( type == 'drawer' ) {
							WAU.Quickshop.hideDrawer();
						} else {
							WAU.Quickshop.hideModal();
						}
					}
        } else if ( isQuickview && config.cart_added_event == 'product_page') {
					if ( quickAdd === true ) {
						var trigger = document.querySelector('.js-quick-adding');
            var quickviewAlt = trigger.dataset.quickviewAlt;
						if ( trigger ) {
							trigger.classList.remove('loading-quickshop');
              if (!quickviewAlt) {

  							WAU.Helpers.fadeOut(trigger.querySelector('.quickview-trigger--plus'));

  							WAU.Helpers.fadeIn(trigger.querySelector('.quickview-trigger--check'), 'inline-block');

              } else {
                addingToCart(trigger);
              }
							setTimeout(function(){

                if (!quickviewAlt) {

  								WAU.Helpers.fadeOut(trigger.querySelector('.quickview-trigger--check'));

  								WAU.Helpers.fadeIn(trigger.querySelector('.quickview-trigger--plus'), 'inline-block');

                } else {
                  addedToCart(trigger);
                }
								trigger.classList.remove('js-quick-adding');
							}, 4000);
						}
					} else {
						WAU.Helpers.fadeIn(context.querySelector(selectors.cartAddedMsg));

						if ( context.getAttribute('data-quickshop-type') == 'drawer' ) {
							context.querySelector(selectors.cartAddedMsg).scrollIntoView();
						}

						setTimeout(function(){
							WAU.Helpers.fadeOut(context.querySelector(selectors.cartAddedMsg));
						}, 4000);
					}
        }

        if (config.cart_action == 'drawer' && config.cart_added_event == 'go_to_active_cart') {
          WAU.AjaxCart.showDrawer(config);
        } else if (config.cart_action == 'modal_cart' && config.cart_added_event == 'go_to_active_cart' )  {
          WAU.AjaxCart.showModal(config);
        } else if (config.cart_action == 'page_only' && config.cart_added_event == 'go_to_active_cart' )  {
          window.location.href = config.cart_url;
				} else if (config.cart_added_event == 'product_page') {
					// WAU.Helpers.fadeIn(context.querySelector(selectors.cartAddedMsg));
					// setTimeout(function () {
					// 	WAU.Helpers.fadeOut(context.querySelector(selectors.cartAddedMsg));
					// }, 4000);
        } else {
          window.location.href = config.cart_url;
        }

        WAU.AjaxCart.updateView(config, Cart);
      });
    }).catch(error => {
      if (error.status == 422) {
				function showErrorMsg() {
					// Show error msg
					WAU.Helpers.fadeIn(context.querySelector('.js-error-msg'));

					// Re-enable add to cart button
					context.querySelector(selectors.addToCart).value = config.add_to_cart;
					context.querySelector(selectors.addToCart).classList.remove('disabled');
					context.querySelector(selectors.addToCart).removeAttribute('disabled', 'disabled');

					setTimeout(function(){
						WAU.Helpers.fadeOut(context.querySelector('.js-error-msg'));
					}, 4000);
				}

				if ( isQuickview ) {
					let type = context.getAttribute('data-quickshop-type');
					let trigger = document.querySelector('.js-quickview-onboard-trigger');

					if ( type == 'drawer' ) {
						WAU.Quickshop.showDrawer(trigger);
						showErrorMsg();
					} else {
						WAU.Quickshop.showModal(trigger);
						showErrorMsg();
					}
				} else {
					showErrorMsg();
				}
      } else {
        console.log(error)
      }
    });
  },
  getAjaxCart: function getAjaxCart(response) {
    const el = document.createElement('div');
    el.innerHTML = response;

    const responseOptions = JSON.parse(el.querySelector('[data-options]').innerHTML);
    const htmls = el.querySelectorAll('[data-html]');

    let html = {};
    if (htmls.length === 1 && htmls[0].getAttribute('data-html') === '') {
      html = htmls[0].innerHTML;
    } else {
      for (let i = 0; i < htmls.length; i++) {
        html[htmls[i].getAttribute('data-html')] = htmls[i].innerHTML;
      }
    }
    let options = responseOptions;

    return html;
  },
  initializeAjaxCart: function initializeAjaxCart(config) {
    let data, url;
    url = config.cart_url + '/?view=ajax';

    fetch(url, data)
    .then(res => res.text())
    .then(response => {
      let html = WAU.AjaxCart.getAjaxCart(response);

      // Replace cart content
      document.querySelectorAll('.js-ajax-cart-content').forEach((item, i) => {
        item.innerHTML = html.content;
      });

    }).then(response => {
      // Init shipping calc
      if ( config.show_calculator && document.querySelector('body').classList.contains('template-cart')) {
        setTimeout(function(){
          ShippingCalculator.init();
        }, 1000);
      }
    }).catch(error => {
      console.log(error)
    });
  },
  updateView: function updateView(config, Cart) {
    let data, url;
    url = config.cart_url + '/?view=ajax';

    fetch(url, data)
    .then(res => res.text())
    .then(response => {
      let html = WAU.AjaxCart.getAjaxCart(response);

      var selectors = {
        cartContent: '.js-ajax-cart-content',
        cartEmpty: '.js-cart-empty',
        cartForm: '.js-cart-form',
        cartAccordion: '.js-cart-accordion',
        cartCount: '.js-cart-count'
      };

      if (Cart.item_count === 0) {
        // Hide form
        document.querySelectorAll(selectors.cartForm).forEach((item, i) => {
          item.classList.add('hide');
        });
        // Show empty msg
        document.querySelectorAll(selectors.cartEmpty).forEach((item, i) => {
          item.classList.remove('hide');
        });
        // Update cart count
        document.querySelectorAll(selectors.cartCount).forEach((item, i) => {
          item.innerHTML = '0';
        });
      } else {
        // Hide empty msg
        document.querySelectorAll(selectors.cartEmpty).forEach((item, i) => {
          item.classList.add('hide');
        });
        // Update cart count
        document.querySelectorAll(selectors.cartCount).forEach((item, i) => {
          item.innerHTML = Cart.item_count;
        });
        // Replace cart page and drawer content
        document.querySelectorAll(selectors.cartContent).forEach((item, i) => {
          item.innerHTML = html.content;
        });
        // Reload accordions
        document.querySelectorAll(selectors.cartAccordion).forEach((item, i) => {
          WAU.Helpers.Accordion(item, '.tlink.has_sub_menu a', '.accordion-content.sub');
          WAU.Helpers.Accordion(item, '.tlink2.has_sub_menu a', '.accordion-content2.sub');
        });

        // Reload events
        WAU.AjaxCart.cartEvents(config);
      }

      // Set Cart Loaded
				setTimeout(function () {
					$('#upsellProductAddToCart').on('click', function(e) {
						e.preventDefault()
						let variantId = $('#upsellProductId').val()
						variantId = parseInt(variantId)
						
						window.MonScripts.updateCartProduct('customer_upsell_product', variantId, refreshCart);
					})
          $('#donationProductAddToCart').on('click', function(e) {
						e.preventDefault()
						let variantId = $('#donationProductId').val()
						variantId = parseInt(variantId)
						
						window.MonScripts.updateCartProduct('customer_donation_product', variantId, refreshCart);
					})
					const refreshCart = function () {
						var evt = new Event('change');
						document.querySelector('[data-item-qty]').dispatchEvent(evt);
						document.body.classList.remove('cart-loaded');
					};
        document.querySelector('body').classList.add('cart-loaded');

					let checkoutBtn = document.querySelector('.ajax-cart__button-submit');
					let deliveryBtn = document.querySelector('.cart-local__buttons');
					let datepickers = document.querySelectorAll('.date-picker');
					let freeShip = document.querySelector('.ajax-cart__free-shipping');
					let errorWrap = document.querySelector('.error-wrap');
					var containsLocalProductEl = document.querySelector('#containsLocalProduct') !== null;
					//var containsLocalProduct = false;
					if (containsLocalProductEl)
					{
						containsLocalProduct = true;
						checkoutBtn.innerHTML = 'Checkout - Choose pickup or delivery';
						checkoutBtn.setAttribute('disabled', 'true');
					} 

					var containsShippingOnlyProductEl = document.querySelector('#containsShippingOnlyProduct') !== null;
					var containsMixedProducts = false;
					//if we have both local-only AND shipping only products in the cart, we need to throw an error
					if (containsShippingOnlyProductEl && containsLocalProductEl)
					{
						console.log('mixed bag!');
						containsMixedProducts = true;
						checkoutBtn.innerHTML = 'Please revise your cart';
						var error_message = 'A la carte fruits are only for pickup or local delivery. Please remove these items to place your order.'
						if (document.body.contains(document.querySelector('.cart-local'))) {
							console.log('local');
							var error_message = 'Certain items must be shipped, while a la carte fruits are only for pickup or local delivery. Please remove or replace the flagged items to place your order.'
						}
						if(deliveryBtn){
						deliveryBtn.style.display = 'none';
						}
						for(var i = 0; i < datepickers.length; i++){
							datepickers[i].style.display = "none";
						}
						if(freeShip){
						freeShip.style.display = "none";
						}
						errorWrap.innerHTML = '<div class="error-inner"><h4 class="tw-text-center"><strong>Oops!</strong> You have items in your cart that cannot be packaged together.</h4><p>'+error_message+'</p></div>';
						checkoutBtn.setAttribute('disabled', 'true');
					} 

					// Local Pickup/Delivery functions
					if (document.body.contains(document.querySelector('.cart-local'))) {
						let pickerActive = false;
						let threshold = parseInt(config.delivery_minium)*100;
						console.log(threshold);
						let message = config.delivery_minium_message;
						let cartTotal = document.querySelector('[data-cart-total]').value
						cartTotal = parseInt(cartTotal)
					
						const datePicker = document.querySelector('[data-date-picker]');
						const datePickerPickup =  document.querySelector('[ data-date-picker-pickup]');
						const datePickerError = document.querySelector('.date-picker__error');
						const checkoutBtn = document.querySelector('.ajax-cart__button-submit');
						
						// Date Picker for Local Delivery
						const initPickerDelivery = function () {
							var data = {
								attributes: {
									'customer_pickup_location_name': null,
									'customer_pickup_date': null

								}
							}
							fetch('/cart/update.js', {
								method: 'POST',
								headers: {
									'Content-Type': 'application/json'
								},
								body: JSON.stringify(data)
							})
							const currentDate = new Date(new Date().toLocaleString("en-US", {timeZone: "America/New_York"}));
							let tomorrow = new Date();
							tomorrow.setDate(currentDate.getDate() + 1);
							//let currentDate = new Date()
							let hour = currentDate.getHours()

							if (hour >= 24){
							 	disableArray.push(tomorrow)
							} 
							$('.date-picker-section').hide();
							if(!containsMixedProducts){
								checkoutBtn.innerHTML = 'Checkout - Choose a date';
							}
							checkoutBtn.setAttribute('disabled', 'false');
							let pickerInputValue =  document.querySelector('.datepicker').value
						
						
							$('.datepicker').pickadate({
								firstDay: 1,
								min: 1,
								max: 30,
								disable: disableArray,
								onSet: function() {
									pickerInputValueTime =  document.querySelector('.datepicker').value
									pickerInputValue = formatDate(pickerInputValueTime)
									prettyDateValue = formatDatePretty(pickerInputValueTime)
									if( pickerInputValue != '' && cartTotal >= threshold) {
										checkoutBtn.innerHTML = 'Checkout';
										checkoutBtn.disabled = false;
										window.MonScripts.updateCartAttribute('customer_order_date', pickerInputValue);
										setTimeout( () => {
											window.MonScripts.updateCartAttribute('display_date', prettyDateValue);	
										}, 1000)
									} else if (pickerInputValue != '' && cartTotal < threshold) {
										checkoutBtn.innerHTML = message;
										checkoutBtn.disabled = true;
										window.MonScripts.updateCartAttribute('customer_order_date', pickerInputValue);
										setTimeout( () => {
											window.MonScripts.updateCartAttribute('display_date', prettyDateValue);	
										}, 1000)
									}
								  }
								})
				
							pickerActive = true;
						};
						
						//TEST

						function splitDatesForDisabling(disabledDatesLiquid) {
						let disableArray = []
						if (disabledDatesLiquid == '')
						{
							return []
						}
						let liquidArray = disabledDatesLiquid.split(';')
					
						liquidArray.forEach( item => {
							let tempArray = [];
							let isItTwoDigetMonth = false;
							let year = item.slice(1, 5);
							let month = item.slice(6,8);
							let day;
							tempArray.push(parseInt(year))
					
							if (isNaN(month) )
							{
								let tempMonth = month.slice(0,1)
								tempArray.push(parseInt(tempMonth))
								isItTwoDigetMonth = false;
							} else {
								isItTwoDigetMonth = true;
								tempArray.push(parseInt(month))
							}
					
							if(isItTwoDigetMonth) {
								day = item.slice(9,11)
								} else {
								day = item.slice(8,10)
							}
					
							if (isNaN(day)) {
								let tempDay = day.slice(0,1)
								tempArray.push(parseInt(tempDay))
							} else {
								tempArray.push(parseInt(day))
							}
							disableArray.push(tempArray)
							
						})

						return disableArray;
						}

						function splitWeekDaysorDisabling (disabledWeekDaysLiquid) {
							let liquidArray = disabledWeekDaysLiquid.split(';')
							let tempArray = [];
							liquidArray.forEach( item => {
								tempArray.push(parseInt(item))
							})
							return tempArray
						}
						function formatDate(date) {
							var d = new Date(date),
								month = '' + (d.getMonth() + 1),
								day = '' + d.getDate(),
								year = d.getFullYear();
						
							if (month.length < 2) 
								month = '0' + month;
							if (day.length < 2) 
								day = '0' + day;
						
							return [month, day, year].join('-');
						}
						function formatDatePretty(date) {
							var days = ['Sunday,','Monday,','Tuesday,','Wednesday,','Thursday,','Friday,','Saturday,'];
							var months = ['January','February','March','April','May','June','July','August','September','October','November','December'];
							

							var d = new Date(date),
								//month = '' + (d.getMonth() + 1),
								numday = '' + d.getDate(),
								day = days[ d.getDay() ],
								month = months[ d.getMonth() ]
								//year = d.getFullYear();
						
							
							if (numday.length < 2) 
								numday = '' + numday;
							console.log('running pretty'+ day + month + numday );
							return [day, month, numday].join(' ');
						}

						// document.querySelector('.ajax-cart__button-submit').addEventListener("click", (e) => {
						// 	e.preventDefault();
						// 	window.location.href = '/checkout'
							
						//   })
						  $('.date-picker-section').hide();

						const initPickerPickup = function () {
							let locationAddress1, locationAddress2, locationCity, locationZip;
							// which btn was selected
							let checkedRadioId = 0
							$('input[name=location-id]').on('change', function(e) {
								$('.date-picker-section').slideUp("slow");
								checkedRadio = $('input[name=location-id]:checked')
								if(!containsMixedProducts){
									checkoutBtn.innerHTML = 'Checkout - Choose a date';
								}
								checkoutBtn.setAttribute('disabled', 'false');
								checkedRadioId = checkedRadio.attr('data-picker-id')
								//$('.datepickerPickup').removeAttr('id');
								$('#datepickerPickupfieldset input, #datepickerPickupfieldset .picker').remove()
								let disabledDatesLiquid = $(`#disabledDates-${checkedRadioId}`).val();
								let disabledWeekDaysLiquid = $(`#disabledWeekDays-${checkedRadioId}`).val();
								let disableDatesArray = splitDatesForDisabling(disabledDatesLiquid);
								let disableWeekDays = splitWeekDaysorDisabling(disabledWeekDaysLiquid);
								disableDatesArray = disableDatesArray.concat(disableWeekDays)
								let locationName = $(`#location-id-${checkedRadioId}`).val();
								window.MonScripts.updateCartAttribute('customer_pickup_location_name', locationName);
                setTimeout(() => logCartState(), 1000);
							
								if (checkedRadioId != 0)
								{
									setTimeout( () => {
										$('#datepickerPickupfieldset').append( `<input name="datepickerPickup" class="datepickerPickup" type="text" placeholder="Select your pickup date">`).show('slow');;
										$('.datepickerPickup').addClass(`datepickerPickup-${checkedRadioId}`)
										$('.date-picker-section').slideDown("slow");
										if ($('.datepickerPickup').length > 1) {
											$('.datepickerPickup')[1].remove()
										}
										initPickerPickupFunctions(checkedRadioId, disableDatesArray)
										//window.MonScripts.updateCartAttribute('customer_order_date', null);
									},200)
								}

								locationAddress1 = $(`#location-addressLine1-${checkedRadioId}`).val();
								locationAddress2 = $(`#location-addressLine2-${checkedRadioId}`).val();
								locationCity = $(`#location-city-${checkedRadioId}`).val();
								locationZip = $(`#location-zipcode-${checkedRadioId}`).val();

							  });
							  if(!containsMixedProducts){  
								checkoutBtn.innerHTML = 'Checkout - Choose a date';
							  }
							checkoutBtn.setAttribute('disabled', 'false');
							let pickerInputValue =  document.querySelector('.datepicker').value

							pickerActive = true;
							// because the subscription options are not passing through to the cart, we cannot use this solution - JM removed

							// document.querySelector('.ajax-cart__button-submit').addEventListener("click", (e) => {
							// 	e.preventDefault();
							// 	let allAttrFromCart;
							
							// 	$.getJSON( "/cart.js")
							// 	.done(function( response ) {
							// 		console.log(response.attributes);
							// 		allAttrFromCart = response.attributes
							// 		const Permalink = new ShopifyPermalink('https://davocadoguy.net/', Cart.items);
							// 		Permalink.setOptions({
							// 			shipping_address: {
							// 				address1: locationAddress1,
							// 				address2: locationAddress2,
							// 				city: locationCity,
							// 				zip: locationZip
							// 			}
							// 		});
							// 		Permalink.setOptions({
							// 			attributes: allAttrFromCart
							// 		});
							// 		Permalink.go();
							// 	})
							// 	.fail(function( jqxhr, textStatus, error ) {
							// 		var err = textStatus + ", " + error;
							// 		console.log( "Request Failed: " + err );
							// 	});
								
							// })
              setTimeout(() => logCartState(), 1000);
						};
						function initPickerPickupFunctions(id, disableDatesArray) {
							const currentDate = new Date(new Date().toLocaleString("en-US", {timeZone: "America/New_York"}));
							let hour = currentDate.getHours()
							let minutes = currentDate.getMinutes()
							let tomorrow = new Date();
							tomorrow.setDate(currentDate.getDate() + 1);
							// if (hour >= 23 && minutes >= 59){
							// 	disableDatesArray.push(tomorrow)
							// } 
							$(`.datepickerPickup-${id}`).pickadate({
								firstDay: 1,
								min: 1,
								max: 30,
								disable: disableDatesArray,
								onSet: function() {
									pickerInputValueTime =  document.querySelector(`.datepickerPickup-${id}`).value
									pickerInputValue = formatDate(pickerInputValueTime)
									prettyDateValue = formatDatePretty(pickerInputValueTime)
									if( pickerInputValueTime != '') {
										checkoutBtn.innerHTML = 'Checkout';
										checkoutBtn.disabled = false;
										window.MonScripts.updateCartAttribute('customer_pickup_date', pickerInputValue);
										setTimeout( () => {
											window.MonScripts.updateCartAttribute('display_date', prettyDateValue);	
										}, 1000)
										
									}
                  // jessie added to make sure the location gets updated
                  checkedRadio = $('input[name=location-id]:checked')
                  checkedRadioId = checkedRadio.attr('data-picker-id')
                  console.log('Checked radio ID:', checkedRadioId);
                  console.log('Looking for element:', `#location-id-${checkedRadioId}`);
                  console.log('Element exists:', $(`#location-id-${checkedRadioId}`).length > 0);
                  let locationName = $(`#location-id-${checkedRadioId}`).val();
                  
                  if (locationName != ''){
                    console.log('found it')
                    console.log('Location name value:', locationName);
								    window.MonScripts.updateCartAttribute('customer_pickup_location_name', locationName);
                    setTimeout(() => logCartState(), 1000);
                  } else {
                    console.log('not seeing this')
                  }

                  


								  }
								})
						}

						if (datePicker.getAttribute('aria-hidden') === 'false') {
							initPickerDelivery();
						}

						if (datePickerPickup.getAttribute('aria-hidden') === 'false') {
							initPickerPickup();
						}


						document.querySelector('[data-cart-local-buttons]').addEventListener('click', function (e) {
							e.preventDefault();
							if (e.target.tagName === 'BUTTON') {
								window.MonScripts.updateCartAttribute('customer_order_type', e.target.innerText, refreshCart);
							} 
						});
					}

      }, 500);
    })
    .catch(error => {
      console.log(error)
    });
  }
}

document.addEventListener('DOMContentLoaded', function() {
  WAU.AjaxCart.init();
});

document.addEventListener('shopify:section:select', function(event){
  WAU.AjaxCart.init();
});
